import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ElementRef, AfterViewInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OverlayModule } from '@angular/cdk/overlay';
import { WidgetEdit } from '../../../../shared/components/widget-edit/widget-edit';

interface FundDataPoint {
    date: string;
    value: number;
    isHighlighted?: boolean;
}

@Component({
    selector: 'app-todays-expense-total',
    standalone: true,
    imports: [CommonModule, WidgetEdit, OverlayModule],
    templateUrl: './todays-expense-total.html',
    styleUrl: './todays-expense-total.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TodaysExpenseTotalComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    @ViewChild('fundCanvas') fundCanvasRef!: ElementRef<HTMLCanvasElement>;

    private resizeObserver: ResizeObserver | null = null;
    private themeObserver: MutationObserver | null = null;
    @Input() fundData: FundDataPoint[] = [
        { date: '6 Jul', value: 242000, isHighlighted: false },
        { date: '7 Jul', value: 238000, isHighlighted: false },
        { date: '8 Jul', value: 234000, isHighlighted: false },
        { date: '9 Jul', value: 230000, isHighlighted: false },

        { date: '10 Jul', value: 165000, isHighlighted: false },
        { date: '11 Jul', value: 166000, isHighlighted: false },
        { date: '12 Jul', value: 167000, isHighlighted: false },
        { date: '13 Jul', value: 168000, isHighlighted: false },
        { date: '14 Jul', value: 168000, isHighlighted: false },

        { date: '15 Jul', value: 155000, isHighlighted: false },
        { date: '16 Jul', value: 165000, isHighlighted: false },
        { date: '17 Jul', value: 172000, isHighlighted: false },

        { date: '18 Jul', value: 35000, isHighlighted: false },
        { date: '19 Jul', value: 35000, isHighlighted: false },
        { date: '20 Jul', value: 35000, isHighlighted: false },
        { date: '21 Jul', value: 35000, isHighlighted: false },
        { date: '22 Jul', value: 35000, isHighlighted: false },

        { date: '23 Jul', value: 182000, isHighlighted: false },
        { date: '24 Jul', value: 179000, isHighlighted: false },
        { date: 'Today', value: 177000, isHighlighted: true }
    ];

    @Input() isEditMode: boolean = false;
    @Input() isAddMode: boolean = false;
    @Output() addWidget = new EventEmitter<void>();
    @Input() size: 'small' | 'medium' | 'half' | 'full' = 'small';
    @Output() editCall = new EventEmitter<any>();
    @Input() index = 0;
    @Input() activeDropdownIndex: number | null = null;
    @Input() getAvailableSizes!: (index: number) => Array<{ value: string; label: string }>;
    @Input() toggleSizeDropdown!: (index: number, event: Event) => void;
    @Input() changeWidgetSize!: (index: number, size: any) => void;
    @Input() removeWidget!: (index: number) => void;
    @Input() title = 'Mutual Funds';
    @Input() stockName = 'Apple Inc. Stock';

    maxValue: number = 242000;
    minValue: number = 0;
    // Y-axis labels with their corresponding values
    yAxisData: { label: string; value: number }[] = [
        { label: '₹2,42L', value: 242000 },
        { label: '₹1,77L', value: 177000 },
        { label: '₹58,42K', value: 58420 },
        { label: '₹0,00L', value: 0 }
    ];
    xAxisLabels: string[] = ['6 Jul', '13 Jul', '20 Jul', 'Today'];
    currentValue: string = '₹1,77L';
    hoveredData: FundDataPoint | null = null;
    tooltipVisible: boolean = false;
    tooltipPosition = { x: 0, y: 0 };
    chartContainerHeight: number = 121; // Default height (matches CSS min-height)
    badgeTopPosition: number = 0; // Position of the value badge on the dashed line

    // Getter for Y-axis labels with calculated top positions (as percentages)
    get yAxisLabelsWithPosition(): { label: string; topPercent: number }[] {
        // Use fixed range based on yAxisData (0 to maxValue) for proper label positioning
        const yAxisMax = 242000; // matches first yAxisData value
        const yAxisMin = 0;      // matches last yAxisData value
        const range = yAxisMax - yAxisMin;

        // Canvas has padding: top: 13px, bottom: 8px
        // Calculate dynamic percentages based on current container height
        const canvasPaddingTop = 13;
        const canvasPaddingBottom = 8;
        const containerHeight = this.chartContainerHeight;

        // Convert pixel padding to percentage of container height
        const chartStartPercent = (canvasPaddingTop / containerHeight) * 100;
        const chartEndPercent = 100 - ((canvasPaddingBottom / containerHeight) * 100);
        const chartRangePercent = chartEndPercent - chartStartPercent;

        return this.yAxisData.map(item => {
            // Calculate position within the chart's data range
            const normalizedPosition = (yAxisMax - item.value) / range;
            // Map to the visible chart area (accounting for canvas padding)
            const topPercent = chartStartPercent + (normalizedPosition * chartRangePercent);
            return { label: item.label, topPercent };
        });
    }

    // Getter for x-axis labels based on size
    get displayXAxisLabels(): string[] {
        if (this.size === 'small') {
            // Only show first and last label for small size
            if (this.xAxisLabels.length >= 2) {
                return [this.xAxisLabels[0], this.xAxisLabels[this.xAxisLabels.length - 1]];
            }
            return this.xAxisLabels;
        }
        return this.xAxisLabels;
    }

    constructor(
        private cdr: ChangeDetectorRef,
        private elementRef: ElementRef
    ) { }

    ngOnInit() {
        this.maxValue = Math.max(...this.fundData.map(d => d.value));
        //this.minValue = Math.min(...this.fundData.map(d => d.value));
        this.minValue = 0;
    }

    ngAfterViewInit() {
        this.setupResizeObserver();
        this.setupThemeObserver();
        setTimeout(() => {
            this.updateChartContainerHeight(this.elementRef.nativeElement.closest('article.widget') as HTMLElement);
            this.drawChart();
            this.cdr.detectChanges();
        }, 0);
    }

    private setupResizeObserver() {
        const widgetElement = this.elementRef.nativeElement.closest('article.widget') as HTMLElement;

        if (!widgetElement) {
            if (!this.isAddMode) {
                console.warn('Could not find parent widget element');
            }
            return;
        }

        let lastHeight = 0;

        this.resizeObserver = new ResizeObserver(() => {
            window.requestAnimationFrame(() => {
                if (!this.resizeObserver) return;

                const isDragging = document.body.classList.contains('is-resizing');
                const resizingWidgetType = document.body.dataset['resizingWidget'];

                // Update if:
                // 1. Not dragging (toggle, initial load, window resize)
                // 2. Dragging THIS widget specifically
                if (!isDragging || resizingWidgetType === 'todayexpensetotal') {
                    const currentHeight = widgetElement.offsetHeight;

                    // Only update if height has actually changed (avoids unnecessary redraws)
                    if (currentHeight !== lastHeight && currentHeight > 0) {
                        lastHeight = currentHeight;
                        this.updateChartContainerHeight(widgetElement);
                        this.drawChart();
                        this.cdr.detectChanges();
                    }
                }
            });
        });
        this.resizeObserver.observe(widgetElement);
    }

    private updateChartContainerHeight(widgetElement: HTMLElement | null): void {
        if (!widgetElement) {
            this.chartContainerHeight = 121; // Default
            return;
        }
        const widgetHeight = widgetElement.offsetHeight;
        // Calculate chart container height as a proportion of widget height
        // Subtracting space for header (~44px), stock name (~24px), x-axis labels (~20px), and padding
        const headerHeight = 44;
        const stockNameHeight = 24;
        const xAxisHeight = 20;
        const paddingHeight = 32;
        const reservedHeight = headerHeight + stockNameHeight + xAxisHeight + paddingHeight;

        // Minimum height of 121px (matches CSS min-height), maximum proportional to widget
        this.chartContainerHeight = Math.max(121, widgetHeight - reservedHeight);
    }

    private setupThemeObserver() {
        // Watch for class changes on body (theme toggle)
        this.themeObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    // Theme changed, redraw chart with new colors
                    setTimeout(() => {
                        this.drawChart();
                        this.cdr.detectChanges();
                    }, 50);
                }
            });
        });

        this.themeObserver.observe(document.body, {
            attributes: true,
            attributeFilter: ['class']
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['size'] && !changes['size'].firstChange) {
            setTimeout(() => {
                // Recalculate chart height based on new size (respects cleared customHeight)
                const widgetElement = this.elementRef.nativeElement.closest('article.widget') as HTMLElement;
                if (widgetElement) {
                    this.updateChartContainerHeight(widgetElement);
                }
                this.drawChart();
                this.cdr.detectChanges();
            }, 0);
        }
    }

    ngOnDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this.themeObserver) {
            this.themeObserver.disconnect();
        }
    }

    private drawChart() {
        if (!this.fundCanvasRef) return;

        const canvas = this.fundCanvasRef.nativeElement;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();

        const widgetContainer = this.elementRef.nativeElement;
        const articleWidget = widgetContainer.closest('article.widget') as HTMLElement;
        const containerHeight = articleWidget ? articleWidget.offsetHeight : 200;

        const canvasHeightRatio = 0.55;
        const calculatedCanvasHeight = containerHeight * canvasHeightRatio;

        canvas.width = rect.width * dpr;
        canvas.height = calculatedCanvasHeight * dpr;
        ctx.scale(dpr, dpr);

        ctx.clearRect(0, 0, rect.width, calculatedCanvasHeight);

        const padding = { left: 8, right: 8, top: 13, bottom: 8 };
        const chartWidth = rect.width - padding.left - padding.right;
        const chartHeight = calculatedCanvasHeight - padding.top - padding.bottom;

        // Get colors from CSS variables for dark/light mode support
        const primaryColor = getComputedStyle(document.body).getPropertyValue('--color-primary1').trim() || '#000000';
        const bgPrimaryLight3 = getComputedStyle(document.body).getPropertyValue('--bg-primarylight3').trim() || 'rgba(0, 0, 0, 0.04)';
        const greenColor = getComputedStyle(document.body).getPropertyValue('--Color-Green').trim() || '#26BC8F';


        // Draw background
        ctx.fillStyle = bgPrimaryLight3;
        ctx.fillRect(padding.left, padding.top, chartWidth, chartHeight);

        // Build path points
        const points: { x: number; y: number }[] = [];
        const numPoints = this.fundData.length;

        this.fundData.forEach((data, index) => {
            const x = padding.left + (index / (numPoints - 1)) * chartWidth;
            const normalizedValue = (data.value - this.minValue) / (this.maxValue - this.minValue);
            const y = padding.top + chartHeight - (normalizedValue * chartHeight);
            points.push({ x, y });
        });

        // Draw gradient fill under the line
        const gradient = ctx.createLinearGradient(0, padding.top, 0, padding.top + chartHeight);
        gradient.addColorStop(0, this.hexToRgba(primaryColor, 0.24));
        gradient.addColorStop(1, this.hexToRgba(primaryColor, 0.06));

        ctx.beginPath();
        ctx.moveTo(points[0].x, padding.top + chartHeight);
        points.forEach(point => {
            ctx.lineTo(point.x, point.y);
        });
        ctx.lineTo(points[points.length - 1].x, padding.top + chartHeight);
        ctx.closePath();
        ctx.fillStyle = gradient;
        ctx.fill();

        // Draw the line
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
            ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.strokeStyle = primaryColor;
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Draw highlighted point (current value indicator)
        const highlightedIndex = this.fundData.findIndex(d => d.isHighlighted);
        if (highlightedIndex !== -1) {
            const highlightPoint = points[highlightedIndex];

            // Set badge position to match the dashed line Y position
            // Scale from canvas coordinates to chart container coordinates
            // The canvas height is calculatedCanvasHeight, but we need position relative to chartContainerHeight
            const canvasToContainerRatio = this.chartContainerHeight / calculatedCanvasHeight;
            this.badgeTopPosition = highlightPoint.y * canvasToContainerRatio;

            // Draw dashed horizontal line
            ctx.setLineDash([4, 4]);
            ctx.beginPath();
            ctx.moveTo(padding.left, highlightPoint.y);
            ctx.lineTo(padding.left + chartWidth, highlightPoint.y);
            ctx.strokeStyle = greenColor;
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.setLineDash([]);
        }
    }

    private hexToRgba(hex: string, alpha: number): string {
        // Handle CSS variables that might be rgb/rgba
        if (hex.includes('rgb')) {
            return hex.replace(')', `, ${alpha})`).replace('rgb', 'rgba');
        }

        // Handle hex colors
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (result) {
            return `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${alpha})`;
        }
        return `rgba(0, 0, 0, ${alpha})`;
    }

    onCanvasHover(event: MouseEvent): void {
        const canvas = this.fundCanvasRef?.nativeElement;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        // Calculate which data point is closest
        const chartWidth = rect.width - 16;
        const numPoints = this.fundData.length;
        const pointSpacing = chartWidth / (numPoints - 1);
        const closestIndex = Math.round((mouseX - 8) / pointSpacing);

        if (closestIndex >= 0 && closestIndex < numPoints) {
            this.hoveredData = this.fundData[closestIndex];
            this.tooltipVisible = true;
            this.tooltipPosition = {
                x: 8 + closestIndex * pointSpacing - 30,
                y: mouseY - 40
            };
        } else {
            this.hideTooltip();
        }
    }

    hideTooltip(): void {
        this.tooltipVisible = false;
        this.hoveredData = null;
    }

    openModal(): void {
        if (this.isEditMode || this.isAddMode) return;
        // Add modal service call if needed
    }

    formatValue(value: number): string {
        if (value >= 100000) {
            return `₹${(value / 100000).toFixed(2)}L`;
        } else if (value >= 1000) {
            return `₹${(value / 1000).toFixed(2)}K`;
        }
        return `₹${value}`;
    }
}
